import { useEffect } from 'react'
import { TemplateEditorHistoryApi } from './useTemplateEditorHistory'

type UseTemplateEditorShortcutsParams = {
    history: TemplateEditorHistoryApi
    moveSelectedBy?: (dx: number, dy: number) => void
    selectedIds: string[]
    nudgeStep: number
    nudgeStepFast: number
    onSelectAll?: () => void
    onClearSelection?: () => void
    onGroup?: () => boolean
    onUngroup?: () => boolean
    onEscape?: () => boolean
}

const isModKey = (event: KeyboardEvent) => event.metaKey || event.ctrlKey

const isEditableTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) {
        return false
    }

    const tag = target.tagName
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable
}

export const useTemplateEditorShortcuts = ({
    history,
    moveSelectedBy,
    selectedIds,
    nudgeStep,
    nudgeStepFast,
    onSelectAll,
    onClearSelection,
    onGroup,
    onUngroup,
    onEscape,
}: UseTemplateEditorShortcutsParams) => {
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (isEditableTarget(event.target)) {
                return
            }

            const mod = isModKey(event)
            const key = event.key.toLowerCase()

            if (event.key === 'Escape') {
                if (onEscape?.()) {
                    return
                }
                onClearSelection?.()
                return
            }

            if (mod && key === 'a') {
                event.preventDefault()
                onSelectAll?.()
                return
            }

            if (mod && key === 'z') {
                event.preventDefault()
                if (event.shiftKey) {
                    history.redo()
                } else {
                    history.undo()
                }
                return
            }

            if (mod && key === 'y') {
                event.preventDefault()
                history.redo()
                return
            }

            if (mod && key === 'c') {
                if (selectedIds.length === 0) {
                    return
                }
                event.preventDefault()
                history.copySelection()
                return
            }

            if (mod && key === 'v') {
                event.preventDefault()
                history.pasteClipboard()
                return
            }

            if (mod && key === 'd') {
                if (selectedIds.length === 0) {
                    return
                }
                event.preventDefault()
                history.duplicateSelection()
                return
            }

            if (mod && key === 'g') {
                event.preventDefault()
                if (event.shiftKey) {
                    onUngroup?.()
                } else {
                    onGroup?.()
                }
                return
            }

            if ((key === 'delete' || key === 'backspace') && selectedIds.length > 0) {
                event.preventDefault()
                history.deleteSelection()
                return
            }

            if (selectedIds.length === 0 || !moveSelectedBy) {
                return
            }

            const step = event.shiftKey ? nudgeStepFast : nudgeStep
            if (key === 'arrowleft') {
                event.preventDefault()
                moveSelectedBy(-step, 0)
            } else if (key === 'arrowright') {
                event.preventDefault()
                moveSelectedBy(step, 0)
            } else if (key === 'arrowup') {
                event.preventDefault()
                moveSelectedBy(0, -step)
            } else if (key === 'arrowdown') {
                event.preventDefault()
                moveSelectedBy(0, step)
            }
        }

        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [
        history,
        moveSelectedBy,
        nudgeStep,
        nudgeStepFast,
        onClearSelection,
        onEscape,
        onGroup,
        onSelectAll,
        onUngroup,
        selectedIds,
    ])
}
